import { useMemo, useState } from 'react';
import {
  LoadingTemplate,
  SiteAdminClinicDetailTemplate,
  SiteAdminNotFoundTemplate,
  SiteAdminServerErrorTemplate,
} from '@/components/templates';
import { useDeleteClinicMutation, useSiteAdminClinicDetail } from '@/hooks/admin';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getTenantSiteTypes } from '@/services/tenants';
import { AppContainer } from '@/components/atoms';

const SiteAdminClinicDetailPage = () => {
  const { site, id } = useParams<{ site: string; id: string }>();
  const { pathname } = useLocation();
  const currentSite = site ?? 'medix';
  const isSuperAdminRoute = pathname.startsWith('/super-admin/admin');
  const [showOutsidePeriod, setShowOutsidePeriod] = useState(false);
  const { data, isLoading, isError, isFetching } = useSiteAdminClinicDetail(id!, {
    showOutsidePeriod,
  });

  const navigate = useNavigate();
  const [showServerError, setShowServerError] = useState(false);

  const { data: siteTypesData } = useQuery({
    queryKey: ['tenants', 'site-types', isSuperAdminRoute ? 'all' : (currentSite ?? '')],
    queryFn: () => getTenantSiteTypes(isSuperAdminRoute ? undefined : (currentSite ?? undefined)),
    enabled: currentSite !== '',
  });
  const siteTypesList = siteTypesData?.data?.site_types ?? [];
  const siteTypeDisplayName = useMemo(() => {
    if (!data?.data?.site_type) return undefined;
    const item = siteTypesList.find(x => x.site_type === data.data.site_type);
    return item?.name ?? undefined;
  }, [data?.data?.site_type, siteTypesList]);

  const deleteMutation = useDeleteClinicMutation({
    onDeleted: () => navigate(`/${currentSite}/admin/clinics`, { replace: true }),
    onServerError: () => setShowServerError(true),
  });

  const handleDelete = () => {
    if (!currentSite || !id) return;
    deleteMutation.mutate({ siteType: currentSite, id: Number(id) });
  };

  const breadcrumbItems = [
    {
      title: '治療院',
      href: `/${currentSite}/admin/clinics`,
    },
    {
      title: ':id',
    },
  ];

  if (isError) return <SiteAdminNotFoundTemplate />;

  if (isLoading || (isFetching && !data)) return <LoadingTemplate />;

  if (showServerError) return <SiteAdminServerErrorTemplate />;

  if (!data) return <SiteAdminNotFoundTemplate />;

  return (
    <main>
      <AppContainer>
        <SiteAdminClinicDetailTemplate
          data={data.data}
          breadcrumbItems={breadcrumbItems}
          clinicId={data.data.id.toString()}
          siteType={currentSite ?? 'medix'}
          siteTypeDisplayName={siteTypeDisplayName}
          isDeleting={deleteMutation.isPending}
          onDelete={handleDelete}
          showOutsidePeriod={showOutsidePeriod}
          onShowOutsidePeriodChange={setShowOutsidePeriod}
          massagersLoading={isFetching}
        />
      </AppContainer>
    </main>
  );
};

export default SiteAdminClinicDetailPage;
